import React, { createContext, useContext, useEffect, useState } from 'react'
import { 
  User,
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth'
import { auth, isMock } from '../lib/firebase'
import { createUserProfile, updateUserProfile as updateFirestoreProfile, getUserProfile } from '../lib/userService'
import type { UserProfile } from '../types/user'

const authInstance: Auth = auth

interface FirebaseError extends Error {
  code: string
}

// Helper to get the correct continue URL for email verification
const getContinueUrl = () => {
  if (typeof window === 'undefined') return ''
  
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const currentOrigin = window.location.origin
  
  // Use environment variable for production URL if available
  let productionUrl = ''
  if (import.meta.env && import.meta.env.VITE_APP_URL) {
    productionUrl = import.meta.env.VITE_APP_URL as string
    // Remove trailing slash if present
    productionUrl = productionUrl.replace(/\/$/, '')
  }
  
  // For localhost development, try using current origin first
  // If it fails (domain not authorized), we'll catch the error and provide instructions
  if (isLocalhost) {
    // Try to use localhost, but user must add localhost to Firebase Console authorized domains
    return currentOrigin + '/login?verified=true'
  }
  
  // For production, prefer environment variable if set and valid
  if (productionUrl && productionUrl !== 'https://SEU-SITE.netlify.app') {
    console.log('Using production URL from env:', productionUrl)
    return productionUrl + '/login?verified=true'
  }
  
  // Fallback to current origin (must be added to authorized domains)
  console.log('Using current origin for continue URL:', currentOrigin)
  return currentOrigin + '/login?verified=true'
}



interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  isAdmin: boolean
  isModerator: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  register: (email: string, password: string, displayName: string, options?: { phoneNumber?: string, city?: string, state?: string }) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>
  resendEmailVerification: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  // List of admin UIDs
  const ADMIN_UIDS = ['tSASTc1EOBYWDwFJDFljtOtaom52']

  // Check if user is admin based on UID or profile field
  const isAdmin = user ? ADMIN_UIDS.includes(user.uid) || userProfile?.isAdmin || userProfile?.role === 'admin' || false : false
  
  // Check if user is moderator based on profile field
  const isModerator = user ? userProfile?.role === 'moderator' || false : false

  useEffect(() => {
    if (isMock) {
      // Mock mode: set loading false immediately
      setLoading(false)
      return
    }
    const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
      setUser(user)
      if (user) {
        // Fetch user profile from Firestore
        try {
          const profile = await getUserProfile(user.uid)
          setUserProfile(profile)
        } catch (error) {
          console.error('Error fetching user profile:', error)
          setUserProfile(null)
        }
      } else {
        setUserProfile(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    if (isMock) {
      // Mock successful login
      const mockUser: User = {
        uid: 'mock-uid-123',
        email,
        displayName: email.split('@')[0],
        emailVerified: true,
        isAnonymous: false,
        metadata: {},
        providerData: [],
        refreshToken: '',
        tenantId: null,
        providerId: 'mock',
        phoneNumber: null,
        photoURL: null,
        delete: async () => {},
        getIdToken: async () => 'mock-token',
        getIdTokenResult: async () => ({ token: 'mock-token', expirationTime: '', issuedAtTime: '', authTime: '', signInProvider: '', signInSecondFactor: null, claims: {} }),
        reload: async () => {},
        toJSON: () => ({}),
      }
      setUser(mockUser)
      // Mock profile
      setUserProfile({
        uid: 'mock-uid-123',
        email,
        displayName: email.split('@')[0],
        photoURL: null,
        phoneNumber: null,
        phoneVerified: false,
        city: null,
        state: null,
        bio: null,
        location: null,
        memberSince: new Date(),
        lastActive: new Date(),
         isVerified: true,
         isAdmin: email.includes('admin'),
         role: email.includes('admin') ? 'admin' : email.includes('moderator') ? 'moderator' : 'user',
         listingsCount: 0,
         groupsCount: 0,
         reviewsCount: 0,
         averageRating: null,
        preferences: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'pt',
          theme: 'light',
        },
        socialLinks: {},
      })
      return
    }
    try {
      await signInWithEmailAndPassword(authInstance, email, password)
    } catch (error) {
      console.error('Login error:', error)
      console.error('Firebase auth instance:', authInstance)
      console.error('Is mock?', isMock)
      throw error
    }
  }

  const loginWithGoogle = async () => {
    if (isMock) {
      // Mock Google login
      const mockUser: User = {
        uid: 'mock-google-uid-' + Date.now(),
        email: 'googleuser@example.com',
        displayName: 'Google User',
        emailVerified: true,
        isAnonymous: false,
        metadata: {},
        providerData: [],
        refreshToken: '',
        tenantId: null,
        providerId: 'google.com',
        phoneNumber: null,
        photoURL: 'https://lh3.googleusercontent.com/a/default',
        delete: async () => {},
        getIdToken: async () => 'mock-google-token',
        getIdTokenResult: async () => ({ token: 'mock-google-token', expirationTime: '', issuedAtTime: '', authTime: '', signInProvider: '', signInSecondFactor: null, claims: {} }),
        reload: async () => {},
        toJSON: () => ({}),
      }
      setUser(mockUser)
      // Mock profile
      setUserProfile({
        uid: mockUser.uid,
        email: 'googleuser@example.com',
        displayName: 'Google User',
        photoURL: 'https://lh3.googleusercontent.com/a/default',
        phoneNumber: null,
        phoneVerified: false,
        city: null,
        state: null,
        bio: null,
        location: null,
        memberSince: new Date(),
        lastActive: new Date(),
        isVerified: true,
        isAdmin: false,
        role: 'user',
        listingsCount: 0,
        groupsCount: 0,
        reviewsCount: 0,
        averageRating: null,
        preferences: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'pt',
          theme: 'light',
        },
        socialLinks: {},
      })
      return
    }
    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      try {
        const result = await signInWithPopup(authInstance, provider)
        const user = result.user
        // Check if user profile exists, create if not
        try {
          await getUserProfile(user.uid)
        } catch (error) {
          await createUserProfile(
            user.uid,
            user.email || '',
            user.displayName || '',
            user.photoURL || undefined,
            {}
          )
        }
      } catch (popupError: unknown) {
        // If popup is blocked, fallback to redirect
        const firebaseError = popupError as FirebaseError
        if (firebaseError.code === 'auth/popup-blocked' || firebaseError.code === 'auth/popup-closed-by-user') {
          await signInWithRedirect(authInstance, provider)
          // The redirect will handle the rest, so we return early
          return
        }
        throw popupError
      }
    } catch (error: unknown) {
      console.error('Google login error:', error)
      // Provide helpful instructions for common errors
      const firebaseError = error as FirebaseError
      if (firebaseError.code === 'auth/unauthorized-domain') {
        const currentDomain = window.location.hostname
        const errorWithInstructions = new Error(
          `Domínio não autorizado: ${currentDomain}. Para usar login com Google em desenvolvimento local, adicione 'localhost' e '127.0.0.1' aos domínios autorizados no Firebase Console:\n\n` +
          '1. Acesse https://console.firebase.google.com\n' +
          '2. Selecione o projeto noeconnect-d1cc1\n' +
          '3. Vá para Authentication → Settings\n' +
          '4. Em "Authorized domains", adicione "localhost" e "127.0.0.1"\n' +
          '5. Salve as alterações\n\n' +
          'Para produção, adicione seu domínio (ex: noeconnect.netlify.app)'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      throw error
    }
  }

  const register = async (email: string, password: string, displayName: string, options?: { phoneNumber?: string, city?: string, state?: string }) => {
    if (isMock) {
      const mockUser: User = {
        uid: 'mock-uid-' + Date.now(),
        email,
        displayName,
        emailVerified: true,
        isAnonymous: false,
        metadata: {},
        providerData: [],
        refreshToken: '',
        tenantId: null,
        providerId: 'mock',
        phoneNumber: null,
        photoURL: null,
        delete: async () => {},
        getIdToken: async () => 'mock-token',
        getIdTokenResult: async () => ({ token: 'mock-token', expirationTime: '', issuedAtTime: '', authTime: '', signInProvider: '', signInSecondFactor: null, claims: {} }),
        reload: async () => {},
        toJSON: () => ({}),
      }
      setUser(mockUser)
      // Mock profile
      setUserProfile({
        uid: mockUser.uid,
        email,
        displayName,
        photoURL: null,
        phoneNumber: options?.phoneNumber || null,
        phoneVerified: false,
        city: options?.city || null,
        state: options?.state || null,
        bio: null,
        location: null,
        memberSince: new Date(),
        lastActive: new Date(),
        isVerified: true,
        isAdmin: false,
        role: 'user',
        listingsCount: 0,
        groupsCount: 0,
        reviewsCount: 0,
         averageRating: null,
        preferences: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'pt',
          theme: 'light',
        },
        socialLinks: {},
      })
      return
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(authInstance, email, password)
      await updateProfile(userCredential.user, { displayName })
      // Send email verification with continue URL
      const actionCodeSettings = {
        url: getContinueUrl(),
        handleCodeInApp: true,
      }
      await sendEmailVerification(userCredential.user, actionCodeSettings)
      // Create user profile in Firestore
      await createUserProfile(
        userCredential.user.uid,
        email,
        displayName,
        userCredential.user.photoURL || undefined,
        options
      )
    } catch (error: unknown) {
      console.error('Registration error:', error)
      // Provide helpful instructions for unauthorized domain errors
      const firebaseError = error as FirebaseError
      if (firebaseError.code === 'auth/unauthorized-continue-uri' || firebaseError.code === 'auth/unauthorized-domain') {
        const currentDomain = window.location.hostname
        const errorType = firebaseError.code === 'auth/unauthorized-continue-uri' ? 'link de verificação' : 'domínio'
        const errorWithInstructions = new Error(
          `Domínio não autorizado para ${errorType}: ${currentDomain}. Para desenvolvimento local, adicione 'localhost' e '127.0.0.1' aos domínios autorizados no Firebase Console:\n\n` +
          '1. Acesse https://console.firebase.google.com\n' +
          '2. Selecione o projeto noeconnect-d1cc1\n' +
          '3. Vá para Authentication → Settings\n' +
          '4. Em "Authorized domains", adicione "localhost" e "127.0.0.1"\n' +
          '5. Salve as alterações\n\n' +
          'Para produção, adicione seu domínio (ex: noeconnect.netlify.app)'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      // Handle too many requests error
      if (firebaseError.code === 'auth/too-many-requests') {
        const errorWithInstructions = new Error(
          'Muitas tentativas de envio de e-mail. Por favor, aguarde alguns minutos antes de tentar novamente.'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      throw error
    }
  }

  const logout = async () => {
    if (isMock) {
      setUser(null)
      setUserProfile(null)
      return
    }
    try {
      await signOut(authInstance)
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    if (isMock) {
      console.log('Mock: password reset email sent to', email)
      return
    }
    try {
      await sendPasswordResetEmail(authInstance, email)
    } catch (error: unknown) {
      console.error('Password reset error:', error)
      const firebaseError = error as FirebaseError
      // Handle too many requests error
      if (firebaseError.code === 'auth/too-many-requests') {
        const errorWithInstructions = new Error(
          'Muitas tentativas de redefinição de senha. Por favor, aguarde alguns minutos antes de tentar novamente.'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      throw error
    }
  }

  const resendEmailVerification = async () => {
    if (isMock) {
      console.log('Mock: email verification sent')
      return
    }
    if (!authInstance.currentUser) throw new Error('No user logged in')
    try {
      const continueUrl = getContinueUrl()
      console.log('Email verification continue URL:', continueUrl)
      console.log('Current user email:', authInstance.currentUser.email)
      
      const actionCodeSettings = {
        url: continueUrl,
        handleCodeInApp: true,
      }
      console.log('Action code settings:', actionCodeSettings)
      await sendEmailVerification(authInstance.currentUser, actionCodeSettings)
    } catch (error: unknown) {
      console.error('Resend email verification error:', error)
      // Provide helpful instructions for unauthorized domain error
      const firebaseError = error as FirebaseError
      if (firebaseError.code === 'auth/unauthorized-continue-uri') {
        const currentDomain = window.location.hostname
        const errorWithInstructions = new Error(
          `Domínio não autorizado para link de verificação: ${currentDomain}. Para desenvolvimento local, adicione 'localhost' e '127.0.0.1' aos domínios autorizados no Firebase Console:\n\n` +
          '1. Acesse https://console.firebase.google.com\n' +
          '2. Selecione o projeto noeconnect-d1cc1\n' +
          '3. Vá para Authentication → Settings\n' +
          '4. Em "Authorized domains", adicione "localhost" e "127.0.0.1"\n' +
          '5. Salve as alterações\n\n' +
          'Para produção, adicione seu domínio (ex: noeconnect.netlify.app)'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      // Handle too many requests error
      if (firebaseError.code === 'auth/too-many-requests') {
        const errorWithInstructions = new Error(
          'Muitas tentativas de envio de e-mail. Por favor, aguarde alguns minutos antes de tentar novamente.'
        ) as FirebaseError
        errorWithInstructions.code = firebaseError.code
        throw errorWithInstructions
      }
      throw error
    }
  }

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (isMock) {
      if (!user) throw new Error('No user logged in')
      const updatedUser: User = {
        ...user,
        displayName: updates.displayName || user.displayName,
        photoURL: updates.photoURL || user.photoURL,
      }
      setUser(updatedUser)
      // Update user profile if exists
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          ...updates,
        })
      }
      return
    }
    if (!authInstance.currentUser) throw new Error('No user logged in')
    try {
      // Update Firebase Auth profile (only displayName and photoURL)
      await updateProfile(authInstance.currentUser, { 
        displayName: updates.displayName || authInstance.currentUser.displayName, 
        photoURL: updates.photoURL || authInstance.currentUser.photoURL 
      })
      // Also update Firestore user profile with all updates
      await updateFirestoreProfile(authInstance.currentUser.uid, updates)
      setUser({ ...authInstance.currentUser } as User)
      // Refresh user profile
      const updatedProfile = await getUserProfile(authInstance.currentUser.uid)
      setUserProfile(updatedProfile)
    } catch (error) {
      console.error('Profile update error:', error)
      throw error
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    isAdmin,
    isModerator,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updateUserProfile,
    resendEmailVerification,
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}